/**
 * Minimal themed Text component for a3s-ui.
 *
 * Deliberately omits font-family configuration — consumers supply fonts via
 * their own provider. Numeric fontWeight values are used directly so the
 * component works with any font or the system default.
 */
import { Text as RNText, type TextProps as RNTextProps, type TextStyle } from "react-native";
import "../theme"; // module augmentation for react-native-unistyles types
import { StyleSheet, useUnistyles } from "react-native-unistyles";
import type { AppColorPalette, AppColorPaletteShades, AppSpacing } from "../theme";
import { useFontFamilyResolver } from "./FontFamilyContext";

// ---------------------------------------------------------------------------
// Public types
// ---------------------------------------------------------------------------

export type TextColorPalette = AppColorPalette | AppColorPaletteShades;

export type TextSize =
  | "xxs/regular"
  | "xxs/medium"
  | "xxs/semibold"
  | "xxs/bold"
  | "xs/regular"
  | "xs/medium"
  | "xs/semibold"
  | "xs/bold"
  | "sm/regular"
  | "sm/medium"
  | "sm/semibold"
  | "sm/bold"
  | "md/regular"
  | "md/medium"
  | "md/semibold"
  | "md/bold"
  | "lg/regular"
  | "lg/medium"
  | "lg/semibold"
  | "lg/bold"
  | "xl"
  | "2xl"
  | "3xl"
  | "4xl"
  | "5xl"
  | "6xl"
  | "7xl"
  | "h1"
  | "h1/medium"
  | "h1/regular"
  | "h1/semibold"
  | "h2"
  | "h2/medium"
  | "h2/regular"
  | "h2/semibold"
  | "h3"
  | "h3/medium"
  | "h3/regular"
  | "h3/semibold"
  | "h4"
  | "h4/medium"
  | "h4/regular"
  | "h4/semibold"
  | "display/xl"
  | "display/lg"
  | "display/md";

export type TextProps = RNTextProps & {
  readonly color?: TextColorPalette;
  readonly fontSize?: TextStyle["fontSize"];
  readonly fontWeight?: TextStyle["fontWeight"];
  readonly textAlign?: TextStyle["textAlign"];
  readonly lineHeight?: TextStyle["lineHeight"];
  readonly letterSpacing?: TextStyle["letterSpacing"];
  readonly textTransform?: TextStyle["textTransform"];
  readonly textDecorationLine?: TextStyle["textDecorationLine"];
  readonly fontStyle?: TextStyle["fontStyle"];
  readonly textStyle?: TextSize;
  readonly truncate?: boolean;
  readonly lineClamp?: number;
  readonly p?: AppSpacing;
  readonly padding?: AppSpacing;
  readonly px?: AppSpacing;
  readonly paddingX?: AppSpacing;
  readonly py?: AppSpacing;
  readonly paddingY?: AppSpacing;
  readonly m?: AppSpacing;
  readonly margin?: AppSpacing;
  readonly mx?: AppSpacing;
  readonly marginX?: AppSpacing;
  readonly my?: AppSpacing;
  readonly marginY?: AppSpacing;
  readonly mb?: AppSpacing;
  readonly marginBottom?: AppSpacing;
  readonly mt?: AppSpacing;
  readonly marginTop?: AppSpacing;
};

// ---------------------------------------------------------------------------
// Text style spec — intentionally no fontFamily so the component stays
// font-agnostic. Consumers configure fonts via their own provider.
// ---------------------------------------------------------------------------

interface TextStyleSpec {
  readonly fontSize: number;
  readonly lineHeight: number;
  readonly fontWeight: TextStyle["fontWeight"];
}

const TEXT_STYLES: Readonly<Record<TextSize, TextStyleSpec>> = {
  "xxs/regular": { fontSize: 10, lineHeight: 12, fontWeight: "400" },
  "xxs/medium": { fontSize: 10, lineHeight: 12, fontWeight: "500" },
  "xxs/semibold": { fontSize: 10, lineHeight: 12, fontWeight: "600" },
  "xxs/bold": { fontSize: 10, lineHeight: 12, fontWeight: "700" },
  "xs/regular": { fontSize: 12, lineHeight: 16, fontWeight: "400" },
  "xs/medium": { fontSize: 12, lineHeight: 16, fontWeight: "500" },
  "xs/semibold": { fontSize: 12, lineHeight: 16, fontWeight: "600" },
  "xs/bold": { fontSize: 12, lineHeight: 16, fontWeight: "700" },
  "sm/regular": { fontSize: 14, lineHeight: 16, fontWeight: "400" },
  "sm/medium": { fontSize: 14, lineHeight: 16, fontWeight: "500" },
  "sm/semibold": { fontSize: 14, lineHeight: 16, fontWeight: "600" },
  "sm/bold": { fontSize: 14, lineHeight: 16, fontWeight: "700" },
  "md/regular": { fontSize: 16, lineHeight: 24, fontWeight: "400" },
  "md/medium": { fontSize: 16, lineHeight: 24, fontWeight: "500" },
  "md/semibold": { fontSize: 16, lineHeight: 24, fontWeight: "600" },
  "md/bold": { fontSize: 16, lineHeight: 24, fontWeight: "700" },
  "lg/regular": { fontSize: 18, lineHeight: 24, fontWeight: "400" },
  "lg/medium": { fontSize: 18, lineHeight: 24, fontWeight: "500" },
  "lg/semibold": { fontSize: 18, lineHeight: 24, fontWeight: "600" },
  "lg/bold": { fontSize: 18, lineHeight: 24, fontWeight: "700" },
  xl: { fontSize: 20, lineHeight: 28, fontWeight: "400" },
  "2xl": { fontSize: 24, lineHeight: 32, fontWeight: "400" },
  "3xl": { fontSize: 30, lineHeight: 36, fontWeight: "400" },
  "4xl": { fontSize: 36, lineHeight: 40, fontWeight: "400" },
  "5xl": { fontSize: 48, lineHeight: 48, fontWeight: "400" },
  "6xl": { fontSize: 60, lineHeight: 60, fontWeight: "400" },
  "7xl": { fontSize: 72, lineHeight: 72, fontWeight: "400" },
  h1: { fontSize: 32, lineHeight: 40, fontWeight: "700" },
  "h1/medium": { fontSize: 32, lineHeight: 40, fontWeight: "500" },
  "h1/regular": { fontSize: 32, lineHeight: 40, fontWeight: "400" },
  "h1/semibold": { fontSize: 32, lineHeight: 40, fontWeight: "600" },
  h2: { fontSize: 28, lineHeight: 36, fontWeight: "700" },
  "h2/medium": { fontSize: 28, lineHeight: 36, fontWeight: "500" },
  "h2/regular": { fontSize: 28, lineHeight: 36, fontWeight: "400" },
  "h2/semibold": { fontSize: 28, lineHeight: 36, fontWeight: "600" },
  h3: { fontSize: 24, lineHeight: 32, fontWeight: "700" },
  "h3/medium": { fontSize: 24, lineHeight: 32, fontWeight: "500" },
  "h3/regular": { fontSize: 24, lineHeight: 32, fontWeight: "400" },
  "h3/semibold": { fontSize: 24, lineHeight: 32, fontWeight: "600" },
  h4: { fontSize: 20, lineHeight: 28, fontWeight: "700" },
  "h4/medium": { fontSize: 20, lineHeight: 28, fontWeight: "500" },
  "h4/regular": { fontSize: 20, lineHeight: 28, fontWeight: "400" },
  "h4/semibold": { fontSize: 20, lineHeight: 28, fontWeight: "600" },
  "display/xl": { fontSize: 64, lineHeight: 72, fontWeight: "400" },
  "display/lg": { fontSize: 48, lineHeight: 56, fontWeight: "400" },
  "display/md": { fontSize: 40, lineHeight: 48, fontWeight: "400" },
} satisfies Record<TextSize, TextStyleSpec>;

// ---------------------------------------------------------------------------
// Color resolution — no `as` casts, explicit narrowing via type predicates
// ---------------------------------------------------------------------------

/** Type predicate: narrows an unknown value to a string-keyed record. */
function isStringRecord(value: unknown): value is Readonly<Record<string, unknown>> {
  return typeof value === "object" && value !== null;
}

interface ThemeColors {
  readonly text: string;
  readonly white: { readonly default: string };
  readonly black: { readonly default: string };
  readonly [key: string]: unknown;
}

interface ThemeParam {
  readonly currentTheme: string;
  readonly colors: ThemeColors;
}

function getTextColor(colorProp: TextColorPalette | undefined, theme: ThemeParam): string {
  if (colorProp === undefined) return theme.colors.text;

  if (colorProp === "white") return theme.colors.white.default;
  if (colorProp === "black") return theme.colors.black.default;

  const isDarkMode = theme.currentTheme === "dark";
  const color: unknown = theme.colors[colorProp];

  if (typeof color === "string") return color;

  if (isStringRecord(color)) {
    const resolved: unknown = color[isDarkMode ? "300" : "700"];
    if (typeof resolved === "string") return resolved;
  }

  return theme.colors.text;
}

// ---------------------------------------------------------------------------
// Style props — extracted for reuse in the StyleSheet factory
// ---------------------------------------------------------------------------

interface TextStyleProps {
  readonly fontSize: TextStyle["fontSize"] | undefined;
  readonly fontWeight: TextStyle["fontWeight"] | undefined;
  readonly fontFamily: string | undefined;
  readonly textAlign: TextStyle["textAlign"] | undefined;
  readonly lineHeight: TextStyle["lineHeight"] | undefined;
  readonly letterSpacing: TextStyle["letterSpacing"] | undefined;
  readonly textTransform: TextStyle["textTransform"] | undefined;
  readonly textDecorationLine: TextStyle["textDecorationLine"] | undefined;
  readonly fontStyle: TextStyle["fontStyle"] | undefined;
  readonly textStyle: TextSize | undefined;
  readonly p: AppSpacing | undefined;
  readonly px: AppSpacing | undefined;
  readonly py: AppSpacing | undefined;
  readonly m: AppSpacing | undefined;
  readonly mx: AppSpacing | undefined;
  readonly my: AppSpacing | undefined;
  readonly mb: AppSpacing | undefined;
  readonly mt: AppSpacing | undefined;
}

const resolveStyles = (props: TextStyleProps) =>
  StyleSheet.create((theme) => {
    const spec = props.textStyle ? TEXT_STYLES[props.textStyle] : undefined;

    return {
      text: {
        fontSize: props.fontSize ?? spec?.fontSize ?? 16,
        fontWeight: props.fontWeight ?? spec?.fontWeight ?? "400",
        fontFamily: props.fontFamily,
        textAlign: props.textAlign,
        lineHeight: props.lineHeight ?? spec?.lineHeight ?? 24,
        letterSpacing: props.letterSpacing,
        textTransform: props.textTransform,
        textDecorationLine: props.textDecorationLine,
        fontStyle: props.fontStyle,
        padding: props.p ? theme.spacing[props.p] : undefined,
        paddingHorizontal: props.px ? theme.spacing[props.px] : undefined,
        paddingVertical: props.py ? theme.spacing[props.py] : undefined,
        margin: props.m ? theme.spacing[props.m] : undefined,
        marginHorizontal: props.mx ? theme.spacing[props.mx] : undefined,
        marginVertical: props.my ? theme.spacing[props.my] : undefined,
        marginBottom: props.mb ? theme.spacing[props.mb] : undefined,
        marginTop: props.mt ? theme.spacing[props.mt] : undefined,
      },
    };
  });

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function Text({
  style,
  color,
  fontSize,
  fontWeight,
  textAlign,
  lineHeight,
  letterSpacing,
  textTransform,
  textDecorationLine,
  fontStyle,
  textStyle,
  truncate,
  lineClamp,
  p,
  padding,
  px,
  paddingX,
  py,
  paddingY,
  m,
  margin,
  mx,
  marginX,
  my,
  marginY,
  mb,
  marginBottom,
  mt,
  marginTop,
  ...rest
}: TextProps) {
  const { theme } = useUnistyles();
  const resolveFontFamily = useFontFamilyResolver();
  const spec = textStyle ? TEXT_STYLES[textStyle] : undefined;
  const resolvedWeight = fontWeight ?? spec?.fontWeight ?? "400";
  const fontFamily = resolveFontFamily?.(resolvedWeight);
  const resolvedColor = getTextColor(color, theme);

  return (
    <RNText
      style={[
        resolveStyles({
          fontSize,
          fontWeight,
          fontFamily,
          textAlign,
          lineHeight,
          letterSpacing,
          textTransform,
          textDecorationLine,
          fontStyle,
          textStyle,
          p: p ?? padding,
          px: px ?? paddingX,
          py: py ?? paddingY,
          m: m ?? margin,
          mx: mx ?? marginX,
          my: my ?? marginY,
          mb: mb ?? marginBottom,
          mt: mt ?? marginTop,
        }).text,
        { color: resolvedColor },
        style,
      ]}
      numberOfLines={lineClamp}
      ellipsizeMode={truncate === true || lineClamp !== undefined ? "tail" : undefined}
      {...rest}
    />
  );
}
